import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  Download,
  FileText,
  ScanLine,
  StickyNote,
  UserRound,
  HeartPulse,
  Pencil,
  ScanSearch,
  Upload,
  Loader2,
  Images,
  Trash2,
  ImagePlus,
  Save,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../../lib/api";
import { StatusPill } from "../../components/app/ui";
import AddPatientModal from "./AddPatientModal";
import {
  CATEGORY_ACCEPT,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  PHOTO_CATEGORIES,
  PhotoCompareSlider,
  groupPhotosByStage,
  isImageRecord,
  isProgressPhoto,
  recordDate,
  recordUrl,
  shortDate,
  sortPhotoRecords,
  stageLabel,
} from "./ProgressPhotoTools";

const NON_PHOTO_CATEGORIES = CATEGORY_ORDER.filter((category) => !PHOTO_CATEGORIES.includes(category));
const inputCls =
  "w-full bg-white border border-slate-200 rounded-xl px-3.5 py-2.5 text-sm text-slate-900 placeholder-slate-400 outline-none transition-[border-color,box-shadow] duration-200 focus:border-[#2563EB] focus:ring-2 focus:ring-[#2563EB]/20";
const labelCls = "block text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400 mb-1.5";

function DetailRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 rounded-xl border border-slate-100 bg-slate-50/60 px-4 py-2.5 text-sm">
      <span className="text-slate-400">{label}</span>
      <span className="text-[#0F172A] text-right truncate">{value || "—"}</span>
    </div>
  );
}

function RecordEditorModal({ record, form, setForm, saving, totalStages, onClose, onSave }) {
  if (!record) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      onClick={onClose}
      data-testid="record-editor-modal"
      className="fixed inset-0 z-[70] bg-slate-900/55 backdrop-blur-sm flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ opacity: 0, y: 20, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 16, scale: 0.97 }}
        onClick={(event) => event.stopPropagation()}
        className="w-full max-w-md rounded-3xl bg-white border border-slate-200 p-6 shadow-[0_30px_70px_rgba(15,23,42,0.25)]"
      >
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <h3 className="font-display text-xl tracking-tight text-[#0F172A]">Edit Photo Details</h3>
            <p className="text-xs text-slate-400 mt-1 truncate">{record.file_name}</p>
          </div>
          <button
            type="button"
            data-testid="record-editor-close"
            onClick={onClose}
            className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
            aria-label="Close editor"
          >
            <X className="w-4 h-4" strokeWidth={1.8} />
          </button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className={labelCls}>Treatment stage</label>
              <input
                type="number"
                min="1"
                max={totalStages}
                data-testid="record-edit-stage"
                className={inputCls}
                value={form.treatment_stage}
                onChange={(event) => setForm((prev) => ({ ...prev, treatment_stage: event.target.value }))}
              />
            </div>
            <div>
              <label className={labelCls}>Capture date</label>
              <input
                type="date"
                data-testid="record-edit-date"
                className={inputCls}
                value={form.capture_date}
                onChange={(event) => setForm((prev) => ({ ...prev, capture_date: event.target.value }))}
              />
            </div>
          </div>
          <div>
            <label className={labelCls}>Photo category</label>
            <select
              data-testid="record-edit-category"
              className={inputCls}
              value={form.category}
              onChange={(event) => setForm((prev) => ({ ...prev, category: event.target.value }))}
            >
              {CATEGORY_ORDER.map((category) => (
                <option key={category} value={category} label={CATEGORY_LABELS[category]} />
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Clinical photo note</label>
            <textarea
              rows={3}
              data-testid="record-edit-description"
              className={`${inputCls} resize-none`}
              placeholder="View, concern, or context for this photo…"
              value={form.description}
              onChange={(event) => setForm((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
          <button
            type="button"
            data-testid="record-edit-save"
            onClick={onSave}
            disabled={saving}
            className="w-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] transition-shadow duration-300 hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)] disabled:opacity-60 flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" strokeWidth={1.8} />}
            Save Photo Details
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

export default function PatientDrawer({ patientId, onClose, onChanged }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState("");
  const [reviewBusy, setReviewBusy] = useState(false);
  const [uploadCategory, setUploadCategory] = useState("intraoral_photos");
  const [uploadStage, setUploadStage] = useState(1);
  const [uploadDate, setUploadDate] = useState("");
  const [uploadDescription, setUploadDescription] = useState("");
  const [compareCategory, setCompareCategory] = useState("intraoral_photos");
  const [compareBeforeId, setCompareBeforeId] = useState("");
  const [compareAfterId, setCompareAfterId] = useState("");
  const [recordEditor, setRecordEditor] = useState(null);
  const [recordForm, setRecordForm] = useState({ treatment_stage: "1", capture_date: "", category: "intraoral_photos", description: "" });
  const [recordSaving, setRecordSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");
  const recordInputs = useRef({});
  const progressInput = useRef(null);

  const load = useCallback(async () => {
    if (!patientId) return;
    setLoading(true);
    setError("");
    try {
      const { data: nextData } = await api.get(`/patients/${patientId}`);
      setData(nextData);
      setUploadStage(nextData.patient?.treatment_stage || 1);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    setData(null);
    setEditOpen(false);
    setPreview(null);
    setRecordEditor(null);
    setDeletingId("");
    setUploadDescription("");
    setUploadDate(new Date().toISOString().slice(0, 10));
    load();
  }, [load]);

  const patient = data?.patient;
  const treatment = data?.treatment;
  const records = useMemo(() => data?.records || [], [data]);
  const totalStages = Number(patient?.treatment_total_stages || 1);
  const pct = patient?.treatment_total_stages
    ? Math.round((patient.treatment_stage / patient.treatment_total_stages) * 100)
    : 0;

  const photoRecords = useMemo(() => sortPhotoRecords(records.filter(isProgressPhoto)), [records]);
  const photoGroups = useMemo(() => groupPhotosByStage(photoRecords), [photoRecords]);
  const clinicalRecords = useMemo(
    () => records.filter((record) => !PHOTO_CATEGORIES.includes(record.category)),
    [records]
  );
  const compareOptions = useMemo(
    () => photoRecords.filter((record) => record.category === compareCategory),
    [photoRecords, compareCategory]
  );
  const compareBefore = compareOptions.find((record) => record.record_id === compareBeforeId) || compareOptions[0] || null;
  const selectedAfter = compareOptions.find((record) => record.record_id === compareAfterId);
  const compareAfter = selectedAfter && selectedAfter.record_id !== compareBefore?.record_id
    ? selectedAfter
    : compareOptions.find((record) => record.record_id !== compareBefore?.record_id) || null;

  useEffect(() => {
    const available = PHOTO_CATEGORIES.find((category) => photoRecords.some((record) => record.category === category));
    if (!photoRecords.some((record) => record.category === compareCategory) && available) {
      setCompareCategory(available);
      return;
    }
    const options = photoRecords.filter((record) => record.category === compareCategory);
    setCompareBeforeId((current) => (options.some((record) => record.record_id === current) ? current : options[0]?.record_id || ""));
    setCompareAfterId((current) => {
      if (options.some((record) => record.record_id === current) && current !== compareBeforeId) return current;
      return (options.find((record) => record.record_id !== compareBeforeId) || options[0])?.record_id || "";
    });
  }, [photoRecords, compareCategory, compareBeforeId]);

  const uploadRecords = async (category, fileList, options = {}) => {
    const arr = Array.from(fileList || []);
    if (arr.length === 0) return;
    let failed = 0;
    for (let i = 0; i < arr.length; i++) {
      setUploading(`Uploading ${i + 1} of ${arr.length}…`);
      const fd = new FormData();
      fd.append("file", arr[i]);
      try {
        const params = { category };
        if (options.stage) params.stage = Number(options.stage);
        if (options.description) params.description = options.description;
        if (options.captureDate) params.capture_date = options.captureDate;
        await api.post(`/patients/${patientId}/records`, fd, {
          params,
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        failed += 1;
        toast.error(formatApiErrorDetail(e.response?.data?.detail));
      }
    }
    setUploading("");
    if (failed === 0) toast.success(arr.length > 1 ? `${arr.length} records uploaded.` : "Record uploaded.");
    setUploadDescription("");
    await load();
    onChanged?.();
  };

  const openRecordEditor = (record) => {
    setRecordEditor(record);
    setRecordForm({
      treatment_stage: String(record.treatment_stage || patient?.treatment_stage || 1),
      capture_date: (record.capture_date || record.created_at || "").slice(0, 10),
      category: record.category || "intraoral_photos",
      description: record.description || "",
    });
  };

  const saveRecordEdit = async () => {
    const stage = Number(recordForm.treatment_stage);
    if (!Number.isFinite(stage) || stage < 1 || stage > totalStages) {
      toast.error(`Stage must be between 1 and ${totalStages}.`);
      return;
    }
    setRecordSaving(true);
    try {
      await api.put(`/records/${recordEditor.record_id}`, {
        treatment_stage: stage,
        capture_date: recordForm.capture_date,
        category: recordForm.category,
        description: recordForm.description,
      });
      toast.success("Photo details updated.");
      setRecordEditor(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRecordSaving(false);
    }
  };

  const deleteRecord = async (record) => {
    if (!window.confirm(`Delete ${record.file_name}? This cannot be undone.`)) return;
    setDeletingId(record.record_id);
    try {
      await api.delete(`/records/${record.record_id}`);
      toast.success("Record deleted.");
      if (preview?.record_id === record.record_id) setPreview(null);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setDeletingId("");
    }
  };

  const openReview = async () => {
    setReviewBusy(true);
    try {
      const { data: pending } = await api.get("/reviews", { params: { patient_id: patientId, status: "pending" } });
      let reviewId = pending[0]?.review_id;
      if (!reviewId) {
        const { data: created } = await api.post("/reviews", { patient_id: patientId });
        reviewId = created.review_id;
        toast.success("AI review started for this patient.");
      }
      navigate(`/reviews?open=${reviewId}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setReviewBusy(false);
    }
  };

  return (
    <AnimatePresence>
      {patientId && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: 640 }}
            animate={{ x: 0 }}
            exit={{ x: 640 }}
            transition={{ type: "spring", stiffness: 300, damping: 34 }}
            data-testid="patient-drawer"
            className="fixed inset-y-0 right-0 z-50 w-full max-w-2xl bg-white border-l border-slate-200 shadow-[0_0_60px_rgba(15,23,42,0.15)] overflow-y-auto"
          >
            <div className="sticky top-0 z-10 flex items-center justify-between px-6 py-5 bg-white/90 backdrop-blur-xl border-b border-slate-100">
              <p className="text-[10px] font-bold tracking-[0.25em] uppercase text-slate-400">Patient Case Workspace</p>
              <button
                type="button"
                data-testid="patient-drawer-close"
                onClick={onClose}
                className="w-9 h-9 rounded-xl border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 transition-colors"
                aria-label="Close"
              >
                <X className="w-4 h-4" strokeWidth={1.8} />
              </button>
            </div>

            {loading ? (
              <div className="p-6 space-y-4" data-testid="patient-drawer-loading">
                {[0, 1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-14 rounded-xl bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : error ? (
              <p className="m-6 text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3">{error}</p>
            ) : patient ? (
              <div className="p-6 space-y-7">
                <div className="flex items-center gap-4">
                  <span className="w-14 h-14 rounded-full bg-gradient-to-br from-[#2563EB] to-[#6366F1] flex items-center justify-center text-lg font-bold text-white shrink-0">
                    {patient.first_name[0]}{patient.last_name[0]}
                  </span>
                  <div className="flex-1 min-w-0">
                    <h2 className="font-display text-xl tracking-tight text-[#0F172A]" data-testid="drawer-patient-name">
                      {patient.first_name} {patient.last_name}
                    </h2>
                    <p className="text-xs text-slate-400 mt-0.5">{patient.display_id} · {patient.treatment_type}</p>
                  </div>
                  <StatusPill status={patient.status} />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    data-testid="edit-patient-button"
                    onClick={() => setEditOpen(true)}
                    className="flex items-center justify-center gap-2 rounded-full border border-[#2563EB]/40 py-3 text-sm font-semibold text-[#1D4ED8] hover:bg-[#2563EB]/5 transition-colors duration-200"
                  >
                    <Pencil className="w-4 h-4" strokeWidth={1.8} />
                    Edit Patient
                  </button>
                  <button
                    type="button"
                    data-testid="drawer-ai-review-button"
                    onClick={openReview}
                    disabled={reviewBusy}
                    className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1] py-3 text-sm font-semibold text-white shadow-[0_8px_20px_rgba(37,99,235,0.25)] hover:shadow-[0_14px_30px_rgba(99,102,241,0.35)] transition-shadow duration-300 disabled:opacity-60"
                  >
                    {reviewBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ScanSearch className="w-4 h-4" strokeWidth={1.8} />}
                    Open AI Review
                  </button>
                </div>

                <section>
                  <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                    <UserRound className="w-3.5 h-3.5" strokeWidth={1.8} /> Patient Details
                  </p>
                  <div className="space-y-2">
                    <DetailRow label="Date of birth" value={patient.date_of_birth} />
                    <DetailRow label="Gender" value={patient.gender} />
                    <DetailRow label="Phone" value={patient.phone} />
                    <DetailRow label="Email" value={patient.email} />
                    <DetailRow label="Assigned doctor" value={patient.assigned_doctor || treatment?.assigned_doctor} />
                  </div>
                </section>

                <section>
                  <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                    <HeartPulse className="w-3.5 h-3.5" strokeWidth={1.8} /> Treatment
                  </p>
                  <div className="rounded-xl border border-slate-100 bg-slate-50/60 p-4">
                    <div className="flex justify-between text-xs text-slate-400 mb-2">
                      <span>Stage {patient.treatment_stage} of {patient.treatment_total_stages}</span>
                      <span>{pct}%</span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-200/70 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-[#2563EB] to-[#6366F1]" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between text-xs text-slate-400 mt-3">
                      <span>Started {patient.treatment_start_date || (treatment?.start_date ? new Date(treatment.start_date).toLocaleDateString() : "—")}</span>
                      {treatment?.expected_end_date && (
                        <span>Est. end {new Date(treatment.expected_end_date).toLocaleDateString()}</span>
                      )}
                    </div>
                  </div>
                </section>

                <section data-testid="treatment-progress-photos">
                  <div className="flex items-center justify-between mb-3">
                    <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB]">
                      <ImagePlus className="w-3.5 h-3.5" strokeWidth={1.8} /> Treatment Progress
                    </p>
                    <span className="text-[11px] text-slate-400" data-testid="progress-photo-count">{photoRecords.length} photo{photoRecords.length === 1 ? "" : "s"}</span>
                  </div>

                  <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50/50 p-4 mb-5" data-testid="progress-upload-panel">
                    <div className="grid grid-cols-2 gap-3 mb-3">
                      <div>
                        <label className={labelCls}>Photo type</label>
                        <select
                          data-testid="progress-upload-category"
                          className={inputCls}
                          value={uploadCategory}
                          onChange={(event) => setUploadCategory(event.target.value)}
                        >
                          {PHOTO_CATEGORIES.map((category) => (
                            <option key={category} value={category} label={CATEGORY_LABELS[category]} />
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className={labelCls}>Treatment stage</label>
                        <input
                          type="number"
                          min="1"
                          max={totalStages}
                          data-testid="progress-upload-stage"
                          className={inputCls}
                          value={uploadStage}
                          onChange={(event) => setUploadStage(event.target.value)}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Capture date</label>
                        <input
                          type="date"
                          data-testid="progress-upload-date"
                          className={inputCls}
                          value={uploadDate}
                          onChange={(event) => setUploadDate(event.target.value)}
                        />
                      </div>
                      <div>
                        <label className={labelCls}>Photo note</label>
                        <input
                          data-testid="progress-upload-description"
                          className={inputCls}
                          placeholder="Optional context"
                          value={uploadDescription}
                          onChange={(event) => setUploadDescription(event.target.value)}
                        />
                      </div>
                    </div>
                    <input
                      ref={progressInput}
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      multiple
                      className="hidden"
                      data-testid="progress-upload-input"
                      onChange={(event) => {
                        uploadRecords(uploadCategory, event.target.files, {
                          stage: uploadStage,
                          description: uploadDescription,
                          captureDate: uploadDate,
                        });
                        event.target.value = "";
                      }}
                    />
                    <button
                      type="button"
                      data-testid="progress-upload-button"
                      onClick={() => progressInput.current?.click()}
                      disabled={Boolean(uploading)}
                      className="w-full rounded-full border border-[#2563EB]/35 py-3 text-sm font-semibold text-[#1D4ED8] hover:bg-[#2563EB]/5 transition-colors duration-200 disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.8} />}
                      {uploading || "Add Progress Photos"}
                    </button>
                  </div>

                  {photoRecords.length === 0 ? (
                    <p className="text-sm text-slate-400 italic" data-testid="progress-photos-empty">No progress photos uploaded yet.</p>
                  ) : (
                    <div className="space-y-5" data-testid="progress-stage-groups">
                      {photoGroups.map(([stage, items]) => (
                        <div key={stage} data-testid={`stage-group-${stage}`}>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-500">
                              {stage === "unstaged" ? "Unstaged" : `Stage ${stage}`}
                            </p>
                            <span className="text-[10px] text-slate-400">{items.length} photo{items.length === 1 ? "" : "s"}</span>
                          </div>
                          <div className="grid grid-cols-2 gap-3">
                            {items.map((record) => (
                              <div key={record.record_id} className="group relative rounded-2xl overflow-hidden border border-slate-200 bg-slate-50 transition-[border-color,box-shadow] duration-200 hover:border-[#2563EB]/40 hover:shadow-[0_10px_24px_rgba(37,99,235,0.12)]">
                                <button
                                  type="button"
                                  data-testid={`progress-photo-${record.record_id}`}
                                  onClick={() => setPreview(record)}
                                  className="block w-full aspect-[4/3]"
                                >
                                  <img
                                    src={recordUrl(record)}
                                    alt={record.file_name}
                                    loading="lazy"
                                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.03]"
                                  />
                                </button>
                                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-900/80 to-transparent px-3 pt-8 pb-2 pointer-events-none">
                                  <p className="text-[10px] font-bold tracking-[0.12em] uppercase text-white">{CATEGORY_LABELS[record.category]}</p>
                                  <p className="text-[10px] text-white/65">{stageLabel(record)} · {shortDate(recordDate(record))}</p>
                                  {record.description && <p className="text-[10px] text-white/75 truncate mt-0.5">{record.description}</p>}
                                </div>
                                <div className="absolute top-2 right-2 flex gap-1.5">
                                  <button
                                    type="button"
                                    data-testid={`progress-edit-${record.record_id}`}
                                    onClick={() => openRecordEditor(record)}
                                    className="w-8 h-8 rounded-full bg-white/90 border border-white/70 flex items-center justify-center text-slate-500 hover:text-[#2563EB] transition-colors"
                                    aria-label="Edit photo details"
                                  >
                                    <Pencil className="w-3.5 h-3.5" strokeWidth={1.8} />
                                  </button>
                                  <button
                                    type="button"
                                    data-testid={`progress-delete-${record.record_id}`}
                                    onClick={() => deleteRecord(record)}
                                    disabled={deletingId === record.record_id}
                                    className="w-8 h-8 rounded-full bg-white/90 border border-white/70 flex items-center justify-center text-slate-500 hover:text-red-500 transition-colors disabled:opacity-50"
                                    aria-label="Delete photo"
                                  >
                                    {deletingId === record.record_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" strokeWidth={1.8} />}
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section data-testid="compare-progress-section">
                  <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                    <Images className="w-3.5 h-3.5" strokeWidth={1.8} /> Compare Progress
                  </p>
                  <div className="rounded-2xl border border-slate-200/80 p-4">
                    <div className="flex flex-wrap gap-2 mb-4" data-testid="compare-category-options">
                      {PHOTO_CATEGORIES.map((category) => {
                        const count = photoRecords.filter((record) => record.category === category).length;
                        return (
                          <button
                            key={category}
                            type="button"
                            data-testid={`compare-category-${category}`}
                            onClick={() => setCompareCategory(category)}
                            className={`rounded-full border px-3 py-1.5 text-[10px] font-semibold tracking-[0.1em] uppercase transition-colors duration-200 ${
                              compareCategory === category
                                ? "border-[#2563EB]/40 bg-[#2563EB]/5 text-[#1D4ED8]"
                                : "border-slate-200 text-slate-400 hover:text-slate-600"
                            }`}
                          >
                            {CATEGORY_LABELS[category]} · {count}
                          </button>
                        );
                      })}
                    </div>
                    {compareOptions.length >= 2 ? (
                      <>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div>
                            <label className={labelCls}>Before</label>
                            <select
                              data-testid="compare-before-select"
                              className={inputCls}
                              value={compareBefore?.record_id || ""}
                              onChange={(event) => setCompareBeforeId(event.target.value)}
                            >
                              {compareOptions.map((record) => (
                                <option
                                  key={record.record_id}
                                  value={record.record_id}
                                  label={`${stageLabel(record)} · ${shortDate(recordDate(record))}`}
                                />
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className={labelCls}>After</label>
                            <select
                              data-testid="compare-after-select"
                              className={inputCls}
                              value={compareAfter?.record_id || ""}
                              onChange={(event) => setCompareAfterId(event.target.value)}
                            >
                              {compareOptions.map((record) => (
                                <option
                                  key={record.record_id}
                                  value={record.record_id}
                                  disabled={record.record_id === compareBefore?.record_id}
                                  label={`${stageLabel(record)} · ${shortDate(recordDate(record))}`}
                                />
                              ))}
                            </select>
                          </div>
                        </div>
                        <PhotoCompareSlider before={compareBefore} after={compareAfter} />
                      </>
                    ) : (
                      <p className="text-sm text-slate-400 italic" data-testid="compare-progress-empty">
                        Upload at least two photos in the same category to compare progress.
                      </p>
                    )}
                  </div>
                </section>

                <section>
                  <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB] mb-3">
                    <StickyNote className="w-3.5 h-3.5" strokeWidth={1.8} /> Clinical Notes
                  </p>
                  {patient.clinical_notes ? (
                    <p className="text-sm text-slate-600 leading-relaxed rounded-xl border border-slate-100 bg-slate-50/60 p-4 whitespace-pre-wrap" data-testid="drawer-clinical-notes">
                      {patient.clinical_notes}
                    </p>
                  ) : (
                    <p className="text-sm text-slate-400 italic">No clinical notes recorded.</p>
                  )}
                </section>

                <section>
                  <div className="flex items-center justify-between mb-3">
                    <p className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-[#2563EB]">
                      <ScanLine className="w-3.5 h-3.5" strokeWidth={1.8} /> Clinical Files
                    </p>
                    <span className="text-[11px] text-slate-400">{clinicalRecords.length} on file</span>
                  </div>

                  <div className="rounded-xl border border-dashed border-slate-300 p-3 mb-4" data-testid="drawer-upload-strip">
                    <div className="flex items-center justify-between gap-2 flex-wrap">
                      <span className="text-xs text-slate-500 flex items-center gap-1.5">
                        <Upload className="w-3.5 h-3.5 text-[#2563EB]" strokeWidth={1.8} />
                        Add clinical files
                      </span>
                      <div className="flex gap-1.5 flex-wrap">
                        {NON_PHOTO_CATEGORIES.map((category) => (
                          <span key={category}>
                            <input
                              ref={(element) => (recordInputs.current[category] = element)}
                              type="file"
                              accept={CATEGORY_ACCEPT[category]}
                              multiple
                              className="hidden"
                              data-testid={`drawer-upload-input-${category}`}
                              onChange={(event) => {
                                uploadRecords(category, event.target.files);
                                event.target.value = "";
                              }}
                            />
                            <button
                              type="button"
                              data-testid={`drawer-upload-btn-${category}`}
                              onClick={() => recordInputs.current[category]?.click()}
                              disabled={Boolean(uploading)}
                              title={CATEGORY_LABELS[category]}
                              className="rounded-lg border border-slate-200 px-2.5 py-1.5 text-[10px] font-semibold text-slate-500 hover:border-[#2563EB]/40 hover:text-[#1D4ED8] hover:bg-[#2563EB]/5 transition-colors duration-200 disabled:opacity-50"
                            >
                              {CATEGORY_LABELS[category]}
                            </button>
                          </span>
                        ))}
                      </div>
                    </div>
                    {uploading && (
                      <p className="flex items-center gap-2 text-[11px] text-slate-500 mt-2" data-testid="drawer-upload-progress">
                        <Loader2 className="w-3 h-3 animate-spin text-[#2563EB]" />
                        {uploading}
                      </p>
                    )}
                  </div>

                  {clinicalRecords.length === 0 ? (
                    <p className="text-sm text-slate-400 italic" data-testid="drawer-records-empty">No clinical files uploaded yet.</p>
                  ) : (
                    <div className="space-y-5" data-testid="drawer-records-list">
                      {NON_PHOTO_CATEGORIES.map((category) => {
                        const catRecords = clinicalRecords.filter((record) => record.category === category);
                        if (catRecords.length === 0) return null;
                        const images = catRecords.filter(isImageRecord);
                        const filesOnly = catRecords.filter((record) => !isImageRecord(record));
                        return (
                          <div key={category} data-testid={`records-group-${category}`}>
                            <p className="text-[10px] font-semibold tracking-[0.18em] uppercase text-slate-400 mb-2">
                              {CATEGORY_LABELS[category]} · {catRecords.length}
                            </p>
                            {images.length > 0 && (
                              <div className="grid grid-cols-3 gap-2 mb-2">
                                {images.map((record) => (
                                  <button
                                    key={record.record_id}
                                    type="button"
                                    data-testid={`record-thumb-${record.record_id}`}
                                    onClick={() => setPreview(record)}
                                    className="group relative aspect-square rounded-xl overflow-hidden border border-slate-200 bg-slate-50 transition-[border-color,box-shadow] duration-200 hover:border-[#2563EB]/40 hover:shadow-[0_8px_20px_rgba(37,99,235,0.12)]"
                                  >
                                    <img
                                      src={recordUrl(record)}
                                      alt={record.file_name}
                                      loading="lazy"
                                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                    />
                                  </button>
                                ))}
                              </div>
                            )}
                            {filesOnly.map((record) => (
                              <a
                                key={record.record_id}
                                href={recordUrl(record)}
                                target="_blank"
                                rel="noreferrer"
                                data-testid={`record-${record.record_id}`}
                                className="group flex items-center gap-3 rounded-xl border border-slate-200/80 px-4 py-3 mb-2 transition-[border-color,box-shadow] duration-200 hover:border-[#2563EB]/30 hover:shadow-[0_6px_18px_rgba(37,99,235,0.08)]"
                              >
                                <span className="w-9 h-9 rounded-lg bg-[#2563EB]/10 flex items-center justify-center shrink-0">
                                  <FileText className="w-4 h-4 text-[#2563EB]" strokeWidth={1.7} />
                                </span>
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium text-[#0F172A] truncate">{record.file_name}</p>
                                  <p className="text-[11px] text-slate-400">
                                    {(record.size / 1024).toFixed(0)} KB · {shortDate(recordDate(record))}
                                  </p>
                                </div>
                                <Download className="w-4 h-4 text-slate-300 group-hover:text-[#2563EB] transition-colors shrink-0" strokeWidth={1.8} />
                              </a>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </section>
              </div>
            ) : null}
          </motion.aside>

          <AnimatePresence>
            {preview && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                onClick={() => setPreview(null)}
                data-testid="record-lightbox"
                className="fixed inset-0 z-[60] bg-slate-900/85 backdrop-blur-sm flex items-center justify-center p-6"
              >
                <div className="max-w-3xl w-full" onClick={(event) => event.stopPropagation()}>
                  <motion.img
                    initial={{ scale: 0.94, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    src={recordUrl(preview)}
                    alt={preview.file_name}
                    className="w-full max-h-[75vh] object-contain rounded-2xl bg-white"
                  />
                  <div className="flex items-center justify-between mt-4 gap-4">
                    <p className="text-sm text-white/80 min-w-0">
                      <span className="block truncate">{preview.file_name}</span>
                      <span className="text-white/40">
                        {CATEGORY_LABELS[preview.category] || preview.category} · {stageLabel(preview)} · {shortDate(recordDate(preview))}
                      </span>
                    </p>
                    <div className="flex gap-2 shrink-0">
                      {isProgressPhoto(preview) && (
                        <button
                          type="button"
                          data-testid="lightbox-edit"
                          onClick={() => openRecordEditor(preview)}
                          className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
                        >
                          Edit
                        </button>
                      )}
                      <a
                        href={recordUrl(preview)}
                        target="_blank"
                        rel="noreferrer"
                        data-testid="lightbox-download"
                        className="rounded-full bg-white/10 border border-white/20 px-4 py-2 text-xs font-semibold text-white hover:bg-white/20 transition-colors"
                      >
                        Download
                      </a>
                      <button
                        type="button"
                        data-testid="lightbox-close"
                        onClick={() => setPreview(null)}
                        className="rounded-full bg-white px-4 py-2 text-xs font-semibold text-slate-900 hover:bg-slate-100 transition-colors"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            <RecordEditorModal
              record={recordEditor}
              form={recordForm}
              setForm={setRecordForm}
              saving={recordSaving}
              totalStages={totalStages}
              onClose={() => setRecordEditor(null)}
              onSave={saveRecordEdit}
            />
          </AnimatePresence>

          <AddPatientModal
            open={editOpen}
            onClose={() => setEditOpen(false)}
            patient={patient}
            existingRecords={records}
            onSaved={async () => {
              setEditOpen(false);
              await load();
              onChanged?.();
            }}
          />
        </>
      )}
    </AnimatePresence>
  );
}
